import { classifySQLiteOperationError, getSQLiteOperationFailureContext } from '@/services/diagnostics/SQLiteOperationDiagnostics';
import { Platform } from 'react-native';
import { APP_CONSTANTS } from '@/config/constants';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';

type SentrySdk = typeof import('@sentry/react-native');
type SentryLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
type SentryTagValue = string | number | boolean | null | undefined;

type BreadcrumbInput = {
  category: string;
  message: string;
  level?: SentryLevel;
  data?: Record<string, unknown>;
  args?: unknown[];
};

type CaptureLoggerErrorInput = {
  module: string;
  subModule?: string;
  message: string;
  args?: unknown[];
};

type SentryUserInput = {
  id?: string | null;
  email?: string | null;
  empresaId?: string | null;
};

type SentryCaptureScopeInput = {
  level?: SentryLevel;
  context?: Record<string, unknown>;
  tags?: Record<string, SentryTagValue>;
  fingerprint?: string[];
  dedupeKey?: string;
  dedupeWindowMs?: number;
};

let cachedSentrySdk: SentrySdk | null | undefined;
let sentryInitAttempted = false;
let sentryInitialized = false;

const DEFAULT_MAX_ARG_LENGTH = 1000;
const DEFAULT_MAX_ARGS = 5;
const DEFAULT_DEDUPE_WINDOW_MS = 30000;
const MAX_DEDUPE_CACHE_ENTRIES = 500;
const captureDedupeCache = new Map<string, number>();

const parseBoolean = (raw: string | undefined, fallback: boolean): boolean => {
  if (!raw) {
    return fallback;
  }
  const normalized = raw.trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }
  return fallback;
};

const parseRate = (raw: string | undefined, fallback: number): number => {
  if (!raw) {
    return fallback;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  if (parsed < 0) {
    return 0;
  }
  if (parsed > 1) {
    return 1;
  }
  return parsed;
};

const getSentryDsn = (): string => (process.env.EXPO_PUBLIC_SENTRY_DSN || '').trim();

const isSentryEnabledByEnv = (): boolean => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    return false;
  }

  const hasDsn = getSentryDsn().length > 0;
  const enabled = parseBoolean(process.env.EXPO_PUBLIC_SENTRY_ENABLED, true);
  return enabled && hasDsn;
};

const getSentryEnvironment = (): string => {
  const env = process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT?.trim();
  if (env) {
    return env;
  }
  return __DEV__ ? 'development' : 'production';
};

const getSentryAppVersion = (): string => {
  const version = APP_CONSTANTS.VERSION?.trim();
  if (!version) {
    return 'unknown';
  }
  return version;
};

const getSentryRelease = (): string | undefined => {
  const appVersion = getSentryAppVersion();
  if (appVersion === 'unknown') {
    return undefined;
  }

  return `app-guincho@${appVersion}`;
};

const getSentrySdk = (): SentrySdk | null => {
  if (cachedSentrySdk !== undefined) {
    return cachedSentrySdk;
  }

  try {
    cachedSentrySdk = require('@sentry/react-native') as SentrySdk;
  } catch {
    cachedSentrySdk = null;
  }

  return cachedSentrySdk;
};

const safeSerialize = (
  value: unknown,
  maxLength: number = DEFAULT_MAX_ARG_LENGTH
): string => {
  if (typeof value === 'string') {
    return value.slice(0, maxLength);
  }

  if (value instanceof Error) {
    return `${value.name}: ${value.message}`.slice(0, maxLength);
  }

  try {
    const json = JSON.stringify(value);
    if (!json) {
      return String(value).slice(0, maxLength);
    }
    return json.slice(0, maxLength);
  } catch {
    return String(value).slice(0, maxLength);
  }
};

const normalizeArgs = (args: unknown[] | undefined): Record<string, string> | undefined => {
  if (!args?.length) {
    return undefined;
  }

  const slicedArgs = args.slice(0, DEFAULT_MAX_ARGS);
  return slicedArgs.reduce<Record<string, string>>((acc, current, index) => {
    acc[`arg_${index + 1}`] = safeSerialize(current);
    return acc;
  }, {});
};

const normalizeContext = (
  context: Record<string, unknown> | undefined
): Record<string, string> | undefined => {
  if (!context) {
    return undefined;
  }

  const entries = Object.entries(context);
  if (!entries.length) {
    return undefined;
  }

  return entries.reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = safeSerialize(value);
    return acc;
  }, {});
};

const normalizeTags = (
  tags: Record<string, SentryTagValue> | undefined
): Record<string, string> | undefined => {
  if (!tags) {
    return undefined;
  }

  const entries = Object.entries(tags)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => [key, String(value)] as const);

  if (!entries.length) {
    return undefined;
  }

  return entries.reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
};

const shouldSkipCaptureByDedupe = (
  dedupeKey: string | undefined,
  dedupeWindowMs: number | undefined
): boolean => {
  if (!dedupeKey) {
    return false;
  }

  const now = Date.now();
  const windowMs =
    typeof dedupeWindowMs === 'number' && Number.isFinite(dedupeWindowMs) && dedupeWindowMs > 0
      ? dedupeWindowMs
      : DEFAULT_DEDUPE_WINDOW_MS;
  const lastCapturedAt = captureDedupeCache.get(dedupeKey);

  if (typeof lastCapturedAt === 'number' && now - lastCapturedAt < windowMs) {
    return true;
  }

  captureDedupeCache.set(dedupeKey, now);
  if (captureDedupeCache.size > MAX_DEDUPE_CACHE_ENTRIES) {
    const oldestKeysToRemove = captureDedupeCache.size - MAX_DEDUPE_CACHE_ENTRIES;
    let removedCount = 0;
    for (const key of captureDedupeCache.keys()) {
      captureDedupeCache.delete(key);
      removedCount += 1;
      if (removedCount >= oldestKeysToRemove) {
        break;
      }
    }
  }

  return false;
};

const applyScopeData = (
  scope: {
    setLevel: (level: SentryLevel) => void;
    setContext: (name: string, context: Record<string, string>) => void;
    setTag: (key: string, value: string) => void;
    setFingerprint: (fingerprint: string[]) => void;
  },
  {
    level,
    context,
    tags,
    fingerprint,
  }: SentryCaptureScopeInput
): void => {
  if (level) {
    scope.setLevel(level);
  }

  const normalizedContext = normalizeContext(context);
  if (normalizedContext) {
    scope.setContext('contexto', normalizedContext);
  }

  const normalizedTags = normalizeTags(tags);
  if (normalizedTags) {
    for (const [key, value] of Object.entries(normalizedTags)) {
      scope.setTag(key, value);
    }
  }

  if (fingerprint?.length) {
    scope.setFingerprint(fingerprint);
  }
};

const getReadySentrySdk = (): SentrySdk | null => {
  if (!sentryInitAttempted) {
    initializeSentry();
  }

  if (!sentryInitialized) {
    return null;
  }

  return getSentrySdk();
};

export const initializeSentry = (): void => {
  if (sentryInitAttempted) {
    return;
  }
  sentryInitAttempted = true;

  if (!isSentryEnabledByEnv()) {
    return;
  }

  const Sentry = getSentrySdk();
  if (!Sentry) {
    return;
  }

  try {
    Sentry.init({
      dsn: getSentryDsn(),
      enabled: true,
      environment: getSentryEnvironment(),
      release: getSentryRelease(),
      debug: parseBoolean(process.env.EXPO_PUBLIC_SENTRY_DEBUG, false),
      tracesSampleRate: parseRate(
        process.env.EXPO_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
        __DEV__ ? 1 : 0.15
      ),
      profilesSampleRate: parseRate(
        process.env.EXPO_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE,
        0
      ),
      attachStacktrace: true,
      sendDefaultPii: false,
      beforeBreadcrumb: breadcrumb => {
        // O createLogger envia o mesmo evento explicitamente, com categoria e argumentos.
        // Sem essa captura explícita, preservamos o console como fonte de contexto.
        if (
          breadcrumb.category === 'console' &&
          parseBoolean(process.env.EXPO_PUBLIC_SENTRY_CAPTURE_LOG_BREADCRUMBS, true) &&
          /^\[\d{6,} \| \d{2}\/\d{2}\/\d{2} -- \d{2}:\d{2}:\d{2}:\d{3}\] \[[^\]\r\n]+\] /.test(breadcrumb.message || '')
        ) {
          return null;
        }
        return breadcrumb;
      },
      integrations: [Sentry.reactNativeTracingIntegration()],
    });

    Sentry.setTag('platform', Platform.OS);
    Sentry.setTag('app_version', getSentryAppVersion());
    sentryInitialized = true;
  } catch (error) {
    console.warn('[SENTRY] Falha ao inicializar SDK:', safeSerialize(error));
  }
};

export const addSentryBreadcrumb = ({
  category,
  message,
  level = 'info',
  data,
  args,
}: BreadcrumbInput): void => {
  if (!parseBoolean(process.env.EXPO_PUBLIC_SENTRY_CAPTURE_LOG_BREADCRUMBS, true)) {
    return;
  }

  const Sentry = getReadySentrySdk();
  if (!Sentry) {
    return;
  }

  const mergedData = {
    ...normalizeContext(data),
    ...normalizeArgs(args),
  };

  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data: Object.keys(mergedData).length ? mergedData : undefined,
  });
};

export const captureSentryMessage = (
  message: string,
  level: SentryLevel = 'error',
  context?: Record<string, unknown>
): void => {
  captureSentryMessageWithScope(message, { level, context });
};

// Agrupamento de cardinalidade fixa; detalhes e identidade ficam apenas no contexto.
const withStableLocalFailureScope = (error: unknown, options: SentryCaptureScopeInput): SentryCaptureScopeInput => {
  try {
    const exactOperations = getSQLiteOperationFailureContext(error);
    const contextOperations = options.context?.sqliteOperations;
    const operations = exactOperations.length ? exactOperations : Array.isArray(contextOperations) ? contextOperations : [];
    const sqliteClass = classifySQLiteOperationError(error).errorClass;
    const aggregate = options.fingerprint?.[0] === 'sync' &&
      ['sync_all_error', 'sync_all_partial', 'push_partial'].includes(options.fingerprint[1]);
    const observedClass = aggregate ? operations.find(operation => operation?.outcome === 'failure' &&
      (operation.errorClass === 'sqlite_busy' || operation.errorClass === 'sqlite_locked'))?.errorClass : undefined;
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    const category = sqliteClass !== 'other' ? sqliteClass :
      /tombstone/i.test(message) ? 'tombstone' : observedClass ?? null;
    if (!category) return options;
    const platform = ['android', 'ios', 'web'].includes(Platform.OS) ? Platform.OS : 'other';
    return {
      ...options,
      fingerprint: ['runtime_failure', category, platform],
      dedupeKey: options.dedupeKey ? `${options.dedupeKey}:${category}:${platform}` : undefined,
      tags: { ...options.tags, failure_category: category, failure_platform: platform },
      context: { ...options.context, ...(exactOperations.length ? { sqliteOperations: exactOperations } : {}) },
    };
  } catch { return options; }
};

export const captureSentryMessageWithScope = (
  message: string,
  options: SentryCaptureScopeInput = {}
): void => {
  const Sentry = getReadySentrySdk();
  if (!Sentry) {
    return;
  }

  options = withStableLocalFailureScope(message, options);
  if (shouldSkipCaptureByDedupe(options.dedupeKey, options.dedupeWindowMs)) {
    return;
  }

  Sentry.withScope(scope => {
    applyScopeData(scope, options);
    Sentry.captureMessage(message);
  });
};

export const captureSentryException = (
  error: unknown,
  context?: Record<string, unknown>
): void => {
  captureSentryExceptionWithScope(error, { context });
};

export const captureSentryExceptionWithScope = (
  error: unknown,
  options: SentryCaptureScopeInput = {}
): void => {
  const Sentry = getReadySentrySdk();
  if (!Sentry) {
    return;
  }

  options = withStableLocalFailureScope(error, options);
  if (shouldSkipCaptureByDedupe(options.dedupeKey, options.dedupeWindowMs)) {
    return;
  }

  Sentry.withScope(scope => {
    applyScopeData(scope, options);
    Sentry.captureException(error);
  });
};

const normalizeSyncMessageForRootCause = (message: string): string => {
  return message
    .replace(
      /Erro ao obter dados da tabela\s+[a-zA-Z0-9_]+\s+\(página\s+\d+\):?/gi,
      'Erro ao obter dados da tabela <table> (página <page>):'
    )
    .replace(/\[trace:[^\]]+\]/gi, '[trace:<trace>]');
};

const stripDynamicLogPrefix = (message: string): string => {
  const withoutPrefix = message.replace(/^\[\d+\s*\|\s*[^\]]+\]\s*/, '');
  return normalizeSyncMessageForRootCause(withoutPrefix);
};

const normalizeDedupePart = (value: string | undefined, maxLength: number): string => {
  if (!value) {
    return 'none';
  }
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'empty';
  }
  return normalized.length > maxLength ? normalized.slice(0, maxLength) : normalized;
};

const getErrorSignatureForDedupe = (error: Error): string => {
  const name = normalizeDedupePart(error.name, 80);
  const message = normalizeDedupePart(error.message, 220);
  const stackLine = normalizeDedupePart(error.stack?.split('\n')[1], 280);
  return `${name}|${message}|${stackLine}`;
};

type ErrorLikePayload = {
  name?: string;
  message: string;
  details?: string;
  hint?: string;
  code?: string;
  stack?: string;
};

const extractErrorLikePayload = (value: unknown): ErrorLikePayload | null => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message || value.name || 'Erro desconhecido',
      stack: value.stack,
    };
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmedValue) as unknown;
        return extractErrorLikePayload(parsed);
      } catch {
        return null;
      }
    }

    return null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const payload = value as Record<string, unknown>;
  const message = typeof payload.message === 'string' ? payload.message.trim() : '';
  if (!message) {
    return null;
  }

  return {
    name: typeof payload.name === 'string' ? payload.name.trim() : undefined,
    message,
    details: typeof payload.details === 'string' ? payload.details.trim() : undefined,
    hint: typeof payload.hint === 'string' ? payload.hint.trim() : undefined,
    code: typeof payload.code === 'string' ? payload.code.trim() : undefined,
    stack: typeof payload.stack === 'string' ? payload.stack : undefined,
  };
};

const findErrorLikePayloadInArgs = (args: unknown[] | undefined): ErrorLikePayload | null => {
  if (!args?.length) {
    return null;
  }

  for (const arg of args) {
    const payload = extractErrorLikePayload(arg);
    if (payload) {
      return payload;
    }
  }

  return null;
};

const getErrorLikeSignatureForDedupe = (payload: ErrorLikePayload): string => {
  const name = normalizeDedupePart(payload.name, 80);
  const message = normalizeDedupePart(payload.message, 220);
  const details = normalizeDedupePart(payload.details?.split('\n')[0], 220);
  const code = normalizeDedupePart(payload.code, 80);
  return `${name}|${message}|${details}|${code}`;
};

const buildSyntheticErrorFromPayload = (payload: ErrorLikePayload): Error => {
  const syntheticError = new Error(payload.message || 'Erro desconhecido');
  syntheticError.name = payload.name || 'ErrorLike';

  if (payload.stack) {
    syntheticError.stack = payload.stack;
    return syntheticError;
  }

  if (payload.details) {
    syntheticError.stack = `${syntheticError.name}: ${syntheticError.message}\n${payload.details}`;
  }

  return syntheticError;
};

export const captureSentryLoggerError = ({
  module,
  subModule,
  message,
  args,
}: CaptureLoggerErrorInput): void => {
  if (!parseBoolean(process.env.EXPO_PUBLIC_SENTRY_CAPTURE_LOG_ERRORS, false)) {
    return;
  }

  const firstError = args?.find(arg => arg instanceof Error) as Error | undefined;
  const errorLikePayload = firstError ? null : findErrorLikePayloadInArgs(args);

  const context = {
    module,
    subModule: subModule || null,
    args: normalizeArgs(args),
  };
  const normalizedMessageForDedupe = stripDynamicLogPrefix(message);
  const tags = {
    flow: 'logger',
    module,
    sub_module: subModule || 'main',
  };

  if (firstError) {
    const errorSignature = getErrorSignatureForDedupe(firstError);
    const dedupeKey = `logger:${module}:${subModule || 'main'}:${normalizedMessageForDedupe}:${errorSignature}`;
    const fingerprint = [
      'logger',
      module,
      subModule || 'main',
      normalizedMessageForDedupe,
      normalizeDedupePart(firstError.name, 80),
      normalizeDedupePart(firstError.message, 220),
    ];

    captureSentryExceptionWithScope(firstError, {
      context: {
        ...context,
        loggerMessage: message,
      },
      tags,
      fingerprint,
      dedupeKey,
    });
    return;
  }

  if (errorLikePayload) {
    const syntheticError = buildSyntheticErrorFromPayload(errorLikePayload);
    const payloadSignature = getErrorLikeSignatureForDedupe(errorLikePayload);
    const dedupeKey = `logger:${module}:${subModule || 'main'}:${normalizedMessageForDedupe}:${payloadSignature}`;
    const fingerprint = [
      'logger',
      module,
      subModule || 'main',
      normalizedMessageForDedupe,
      normalizeDedupePart(errorLikePayload.name, 80),
      normalizeDedupePart(errorLikePayload.message, 220),
      normalizeDedupePart(errorLikePayload.code, 80),
    ];

    captureSentryExceptionWithScope(syntheticError, {
      context: {
        ...context,
        loggerMessage: message,
        errorLike: errorLikePayload,
      },
      tags: {
        ...tags,
        error_kind: 'error_like',
        error_code: errorLikePayload.code || 'none',
      },
      fingerprint,
      dedupeKey,
    });
    return;
  }

  const dedupeKey = `logger:${module}:${subModule || 'main'}:${normalizedMessageForDedupe}`;
  const fingerprint = ['logger', module, subModule || 'main', normalizedMessageForDedupe];

  captureSentryMessageWithScope(message, {
    level: 'error',
    context,
    tags,
    fingerprint,
    dedupeKey,
  });
};

export const setSentryUser = ({ id, email, empresaId }: SentryUserInput): void => {
  const Sentry = getReadySentrySdk();
  if (!Sentry) {
    return;
  }

  if (!id && !email) {
    Sentry.setUser(null);
    Sentry.setTag('user_id', 'anonymous');
    Sentry.setTag('empresa_id', 'none');
    return;
  }

  Sentry.setUser({
    id: id || undefined,
    email: email || undefined,
    empresa_id: empresaId || undefined,
  });
  Sentry.setTag('user_id', id || 'sem_id');
  Sentry.setTag('empresa_id', empresaId || 'none');
};

export const clearSentryUser = (): void => {
  const Sentry = getReadySentrySdk();
  if (!Sentry) {
    return;
  }
  Sentry.setUser(null);
  Sentry.setTag('user_id', 'anonymous');
  Sentry.setTag('empresa_id', 'none');
};
